// Cross-domain configuration for CastReady ecosystem
// Note: Legal pages remain on hub until explicit migration

export const DOMAINS = {
  hub: 'https://hub.castready.org',
  main: 'https://castready.org',
} as const;

// Page ownership - which domain is canonical
// IMPORTANT: Legal pages stay on hub during migration phase
export const PAGE_OWNERSHIP: Record<string, 'main' | 'hub'> = {
  // Hub owns ALL current pages during migration
  '/pricing': 'hub',
  '/platforms': 'hub',
  '/for': 'hub',
  '/compare': 'main',
  '/training-kits': 'hub',
  '/faq': 'hub',
  '/support': 'hub',
  '/blog': 'main',           // Blog now lives on marketing site
  '/press': 'main',          // Press redirects to marketing press-kit
  '/about': 'main',          // Marketing-domain owned
  '/partnerships': 'main',   // Marketing-domain owned
  '/contact': 'main',        // Marketing-domain owned
  '/privacy-policy': 'hub',  // Stays on hub until migrated
  '/terms-of-service': 'hub',// Stays on hub until migrated
  '/accessibility': 'hub',   // Stays on hub until migrated
  '/ai-fairness': 'hub',     // Stays on hub until migrated
  '/ai-philosophy': 'hub',   // Stays on hub until migrated
  
  // Main site will own these corporate pages (new)
  '/careers': 'main',
  '/investors': 'main',
  '/press-kit': 'main',      // Corporate press kit on main site
  '/newsroom': 'main',
};

// External navigation links to main site
export const MAIN_SITE_LINKS = {
  // Core marketing
  home: 'https://castready.org',
  about: 'https://castready.org/about',
  howItWorks: 'https://castready.org/how-it-works',
  pricing: 'https://castready.org/pricing',
  contact: 'https://castready.org/contact',
  support: 'https://castready.org/support',

  // Wedge landing pages
  coach: 'https://castready.org/coach',
  studios: 'https://castready.org/studios',
  studiosAgencies: 'https://castready.org/studios-agencies',
  actors: 'https://castready.org/actors',
  partners: 'https://castready.org/partners',
  boxes: 'https://castready.org/boxes',
  boxesWellnessReset: 'https://castready.org/boxes/wellness-reset',
  boxesCastingInsights: 'https://castready.org/boxes/casting-insights',
  boxesBritishModernRP: 'https://castready.org/boxes/british-modern-rp',

  // Conversion
  demo: 'https://castready.org/demo',
  redeem: 'https://castready.org/redeem',

  // Content
  blog: 'https://castready.org/blog',
  testimonials: 'https://castready.org/testimonials',
  newsroom: 'https://castready.org/newsroom',
  pressKit: 'https://castready.org/press-kit',

  // Investor / careers
  careers: 'https://castready.org/careers',
  investors: 'https://castready.org/investors',
} as const;

// Helper to determine if link should be external
export const isExternalLink = (href: string): boolean => {
  return href.startsWith('http://') || href.startsWith('https://');
};

// Pages that exist on both domains (shared content)
// These need cross-domain canonical signals
export const SHARED_PAGES: Record<string, {
  canonical: 'main' | 'hub';
  existsOnBoth: boolean;
  mainPath?: string; // Optional: if canonical path differs between domains
}> = {
  // Legal pages - currently hub is canonical, both domains may host
  '/privacy-policy': { canonical: 'hub', existsOnBoth: true },
  '/terms-of-service': { canonical: 'hub', existsOnBoth: true },
  '/accessibility': { canonical: 'hub', existsOnBoth: true },
  '/ai-fairness': { canonical: 'hub', existsOnBoth: true },
  '/ai-philosophy': { canonical: 'hub', existsOnBoth: true },

  // Marketing-owned pages still reachable on hub via stubs/redirects.
  // Canonical points to the marketing domain so hub stops competing on these queries.
  '/about': { canonical: 'main', existsOnBoth: true },
  '/partnerships': { canonical: 'main', existsOnBoth: true },
  '/contact': { canonical: 'main', existsOnBoth: true },
  '/press': { canonical: 'main', existsOnBoth: true, mainPath: '/press-kit' },
  '/blog': { canonical: 'main', existsOnBoth: true },
};

// Get canonical domain for a path
export const getCanonicalDomain = (path: string): string => {
  if (PAGE_OWNERSHIP[path]) {
    return DOMAINS[PAGE_OWNERSHIP[path]];
  }
  for (const [prefix, owner] of Object.entries(PAGE_OWNERSHIP)) {
    if (path.startsWith(prefix)) {
      return DOMAINS[owner];
    }
  }
  return DOMAINS.hub; // Default to hub
};

// Get the canonical URL for a path (cross-domain aware)
export const getCanonicalUrl = (path: string): string => {
  const sharedPage = SHARED_PAGES[path];
  if (sharedPage) {
    const targetPath = sharedPage.mainPath || path;
    return `${DOMAINS[sharedPage.canonical]}${targetPath}`;
  }
  
  // Check PAGE_OWNERSHIP
  if (PAGE_OWNERSHIP[path]) {
    return `${DOMAINS[PAGE_OWNERSHIP[path]]}${path}`;
  }
  
  // Check prefix matches
  for (const [prefix, owner] of Object.entries(PAGE_OWNERSHIP)) {
    if (path.startsWith(prefix + '/')) {
      return `${DOMAINS[owner]}${path}`;
    }
  }
  
  // Default to hub
  return `${DOMAINS.hub}${path}`;
};

// Get cross-domain alternate URL if page is shared
export const getCrossDomainAlternate = (path: string): string | null => {
  const sharedPage = SHARED_PAGES[path];
  if (sharedPage?.existsOnBoth) {
    // Return the OTHER domain's URL with correct path
    const alternateDomain = sharedPage.canonical === 'hub' ? 'main' : 'hub';
    const alternatePath = alternateDomain === 'main' && sharedPage.mainPath 
      ? sharedPage.mainPath 
      : path;
    return `${DOMAINS[alternateDomain]}${alternatePath}`;
  }
  return null;
};
